/* eslint-disable no-alert */
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useRef, Children } from 'react';
import cn from 'classnames';
// import child components
import HamburgerMenu from './HamburgerMenu.jsx';
import ExoMenu from './ExoMenu.jsx';
import MenuDispatcher from './MenuDispatcher.jsx';
import useStopTransitionsOnResize from './useStopTransitionsOnResize.js';
import useIsMobile from '../../hooks/useIsMobile.js';
import LdsIcon from '../LdsIcon/index.jsx';

export default function LdsHeader({
  menuAlignment = 'right',
  exoMenu = [],
  menuItems = [],
  logoSize = 'sm',
  sticky = false,
  closeMegaMenuLabel = 'Close Mega Menu',
  children,
  renderChildrenPosition = 'after',
  childMobilePadding = '0px',
  customLogo = null,
  className = '',
  ...rest
}) {
  useStopTransitionsOnResize();
  const isMobile = useIsMobile(900);

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const closeMenuButtonRef = useRef(null);
  const openMenuButtonRef = useRef(null);
  function toggleMobileMenu() {
    if (isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
      // If the menu is open, we focus the open menu button after closing it.
      if (openMenuButtonRef.current) {
        openMenuButtonRef.current.focus();
      }
    } else {
      setIsMobileMenuOpen(true);
      // If the menu is closed, we focus the close menu button after opening it.
      if (closeMenuButtonRef.current) {
        closeMenuButtonRef.current.focus();
      }
    }
  }

  function getMenuType(item) {
    if (item?.megaMenu?.megaMenuColumns?.length) {
      return 'megaMenu';
    }
    if (item?.submenuItems?.length) {
      return 'submenu';
    }
    return 'link';
  }

  // This function is used to render custom components in the header menu.
  // It will render the children either before or after the header items.
  // React.Children is used here to allow us to wrap the rerendered children in
  // the same li and div elements that are used by other header elements.
  const mobilePadding = isMobile ? childMobilePadding : '0px';
  function renderCustomChildren(children, renderChildrenPosition, position) {
    if (children && renderChildrenPosition === position) {
      return Children.map(children, (child, index) => (
        <li className="lds-header-menu-item " key={`customComponent-${index}`}>
          <div className="lds-header-menu-item-content custom-item">
            <div style={{ padding: mobilePadding }}>{child}</div>
          </div>
        </li>
      ));
    }
    return null;
  }

  return (
    <div
      className="menu-wrapper"
      style={{ position: sticky ? 'sticky' : 'relative', top: 0 }}
    >
      <header
        id="header"
        className={cn(
          'lds-header',
          `lds-header-align-${menuAlignment}`,
          className
        )}
        style={{ position: sticky ? 'sticky' : 'relative' }}
        {...rest}
      >
        <span
          id="a11y-state-announcer"
          role="status"
          className="visually-hidden"
        ></span>
        {exoMenu.length > 0 && <ExoMenu itemList={exoMenu} />}
        <div className="lds-header-main  wrapper">
          <div className="lds-header-logo-wrapper">
            {customLogo || (
              <lds-lilly-logo
                paddingOverrideClass="pl-0 pr-0"
                size={logoSize}
              />
            )}
            <HamburgerMenu
              toggleMobileMenu={toggleMobileMenu}
              openMenuButtonRef={openMenuButtonRef}
              isMobileMenuOpen={isMobileMenuOpen}
            />
          </div>
          <div
            className={`lds-header-menu-container  header-mobile ${menuAlignment}`}
            // hidden if in desktop view otherwise toggle based on isMobileMenuOpen
            aria-hidden={!isMobile ? true : !isMobileMenuOpen}
          >
            <nav
              aria-label="Primary"
              className={`lds-header-menu-wrapper ${
                isMobileMenuOpen ? 'toggle-mobile-menu' : ''
              }`}
            >
              <div className="lds-header-menu-container-logo">
                <div className="row align-items-center justify-content-between">
                  <div className="col-auto header-logo">
                    {customLogo || (
                      <lds-lilly-logo
                        paddingOverrideClass="pl-0 pr-0"
                        size={logoSize}
                      />
                    )}
                  </div>
                  <div className="col-auto">
                    <button
                      ref={closeMenuButtonRef}
                      id="lds-close-menu-button"
                      data-testid="lds-close-menu-button"
                      onClick={toggleMobileMenu}
                      type="button"
                      className="lds-button-base lds-header-toggle toggleMobileMenu"
                    >
                      <span className="lds-header-toggle-text"> Close </span>
                      <LdsIcon decorative name="X" inline />
                    </button>
                  </div>
                </div>
              </div>
              <div className="lds-header-menu-scroll-container">
                <ul
                  className={`lds-header-menu lds-header-menu-primary lds-header-menu-${menuAlignment}`}
                >
                  {renderCustomChildren(
                    children,
                    renderChildrenPosition,
                    'before'
                  )}
                  {menuItems.map((item, index) => (
                    <MenuDispatcher
                      key={index}
                      index={index}
                      menuType={getMenuType(item)}
                      {...item}
                      isMobile={isMobile}
                      closeMegaMenuLabel={closeMegaMenuLabel}
                    />
                  ))}
                  {renderCustomChildren(
                    children,
                    renderChildrenPosition,
                    'after'
                  )}
                </ul>
              </div>
            </nav>
          </div>
        </div>
      </header>
    </div>
  );
}

const MenuItemsShape = PropTypes.shape({
  itemText: PropTypes.string.isRequired,
  newTab: PropTypes.bool,
  externalCallback: PropTypes.func,
  href: PropTypes.string,
  type: PropTypes.string,
  ariaLabel: PropTypes.string,
  megaMenu: PropTypes.shape({
    options: PropTypes.shape({
      additionalLink: PropTypes.shape({
        itemText: PropTypes.string.isRequired,
        href: PropTypes.string,
        iconName: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
    megaMenuColumns: PropTypes.arrayOf(
      PropTypes.shape({
        menuName: PropTypes.string.isRequired,
        menuItems: PropTypes.arrayOf(
          PropTypes.shape({
            itemText: PropTypes.string.isRequired,
            ariaLabel: PropTypes.string,
            externalCallback: PropTypes.func,
            iconName: PropTypes.string,
            href: PropTypes.string,
            download: PropTypes.bool,
          })
        ).isRequired,
      })
    ).isRequired,
  }),
  submenuItems: PropTypes.arrayOf(
    PropTypes.shape({
      itemText: PropTypes.string.isRequired,
      ariaLabel: PropTypes.string,
      externalCallback: PropTypes.func,
      iconName: PropTypes.string,
      href: PropTypes.string,
      download: PropTypes.bool,
    })
  ),
});
LdsHeader.propTypes = {
  menuAlignment: PropTypes.oneOf(['left', 'right', 'even']),
  exoMenu: PropTypes.arrayOf(MenuItemsShape),
  menuItems: PropTypes.arrayOf(MenuItemsShape),
  logoSize: PropTypes.oneOf(['sm', 'md', 'lg']),
  sticky: PropTypes.bool,
  closeMegaMenuLabel: PropTypes.string,
  renderChildrenPosition: PropTypes.oneOf(['before', 'after']),
  childMobilePadding: PropTypes.string,
};
